import { nextTick, type UnwrapNestedRefs } from 'vue';
import type { GIMovie } from 'src/models/geoItem';
import type { MoviePlayerState } from 'src/models/moviePlayer';
import {
  getMovieFileUrlObj,
  findFirstSeekPointFromHitMgiMap,
  getNextMovieGeoIndex,
  initMovieFileUrlObjState,
} from 'src/lib/moviePlayerHelper';
import { dtFormat } from 'src/lib/dateHelper';
import type { PlaybackControlContext } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerPlaybackControl';
import type { PlaybackVjsContext } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerPlaybackVjs';

export interface MovieTransitionConfig {
  // ロード完了ポーリング対象vid一覧。frontを先頭にする（体感パフォーマンスのため）
  loadVideoIds: () => string[];
}

export interface MovieTransitionContext {
  invalidateCurrentMovie: () => void;
  resetVideoPlayers: () => Promise<void>;
  setCurrentMovie: (movie: GIMovie, movieIdx: number, playOffsetMsec: number) => Promise<void>;
  playPreviousMovie: () => Promise<void>;
  playNextMovie: () => Promise<void>;
  setVideoControlPropertiesToDefault: () => void;
  findFirstSeekPoint: () => number;
  // mgi更新ロジック。
  // timeupdateハンドラ（useMoviePlayerTimeUpdate）からも呼ばれるため公開する。
  trySetNextMovieGeoIndex: () => void;
}

// 動画遷移（現在動画の切替/無効化/リセット/初回シーク位置算出）を担うcomposable。
// 再生状態の一時停止・再開はcontrol.pauseUI/playUIに委譲し、
// リセット時のvjsエラークリアはvjs.clearVjsErrorsに委譲する。
export function useMoviePlayerMovieTransition(
  state: UnwrapNestedRefs<MoviePlayerState>,
  config: MovieTransitionConfig,
  control: Pick<PlaybackControlContext, 'pauseUI' | 'playUI' | 'getUIPlayState'>,
  vjs: Pick<PlaybackVjsContext, 'clearVjsErrors'>,
): MovieTransitionContext {
  const invalidateCurrentMovie = () => {
    state.movieList.current.currentMovie = null;
    state.movieList.current.currentMovieIdx = -1;
    state.movieList.current.movieFileUrlObj = initMovieFileUrlObjState();
    state.movieList.current.isFirstMovie = false;
    state.movieList.current.isLastMovie = false;
    state.movieList.current.isCurrentMovieReady = false;
    state.geo.currentMgi = null;
    state.geo.currentMgiIdx = -1;
    state.geo.currentMovieCurrentDt = null;
    state.movieList.current.continuedMovies = [];
    state.movieList.current.isMovieEnded = false;
  };
  const resetVideoPlayers = async() => {
    state.playback.videoControlProperties.reset++;
    vjs.clearVjsErrors();
    invalidateCurrentMovie();
  };
  const updateTitleBarTexts = () => {
    if (!state.geo.currentMgi) return;

    const mgi = state.geo.currentMgi;
    const dateDisp = dtFormat(mgi.ts, 'yyyy/mm/dd HH:MM:SS') + '~';
    const locationDisp = mgi.locationDisp;
    state.playback.titleBarTexts = { dateDisp, locationDisp };
  };
  const trySetNextMovieGeoIndex = () => {
    if (!state.movieList.current.currentMovie) return;

    const next = getNextMovieGeoIndex(
      state.movieList.current.currentMovie.id,
      state.movieList.current.currentMovie.movieGeoIndices,
      state.geo.currentMgiIdx,
    );
    if (next) {
      state.geo.currentMgi = next.mgi;
      state.geo.currentMgiIdx = next.idx;
    }
    updateTitleBarTexts();
  };
  const setCurrentMovie = async(movie: GIMovie, movieIdx: number, playOffsetMsec: number) => {
    const realOffsetMsec = (movie.playStartOffsetMsec || 0) + playOffsetMsec;
    // 一旦再生を止める
    const playState = control.getUIPlayState();
    control.pauseUI();
    state.movieList.current.isCurrentMovieReady = false;
    if (!state.movieList.current.currentMovie || state.movieList.current.currentMovie.id !== movie.id) {
      // 現在再生しているのと異なる動画なので取得する
      state.movieList.current.currentMovie = movie;
      state.movieList.current.currentMovieIdx = movieIdx;
      try {
        state.movieList.current.movieFileUrlObj = await getMovieFileUrlObj(state.movieList.current.currentMovie.id);
      } catch (e) {
        console.error('動画ファイルURLの取得に失敗しました', e);
        invalidateCurrentMovie();
        return;
      }
      state.movieList.current.isFirstMovie = movieIdx === 0;
      state.movieList.current.isLastMovie = !!state.movieList.listSelection.currentMovieList &&
        movieIdx === state.movieList.listSelection.currentMovieList.movies.length - 1;
      state.playback.videoControlProperties.loadMovie++;
      const promises = [];
      // frontのロードを最初にすることで
      // 少しだけ体感上のパフォーマンスがよくなるかもしれない.
      // (ほぼ変わらないだろうが.)
      for (const vid of config.loadVideoIds()) {
        state.playback.vjsLoadedData[vid] = false;
        promises.push(new Promise<void>((resolve) => {
          const loopFunc = () => {
            if (state.playback.vjsLoadedData[vid]) return resolve();

            window.setTimeout(loopFunc, 100);
          };
          loopFunc();
        }));
      }
      await Promise.all(promises);
    }
    // ロード中に画面が閉じられた場合のエラーを回避するため
    if (!state.movieList.current.currentMovie) return;

    // mgiを計算する
    const movieId = state.movieList.current.currentMovie.id;
    const mgis = state.movieList.current.currentMovie.movieGeoIndices.filter(e => e.movieId === movieId);
    let targetMgi = null;
    let targetMgiIdx = -1;
    for (let i = 0, len = mgis.length; i < len; i++) {
      targetMgi = mgis[i];
      targetMgiIdx = i;
      if (targetMgi.endMsecDiff && realOffsetMsec < targetMgi.endMsecDiff) {
        break;
      }
    }
    state.geo.currentMgi = targetMgi;
    state.geo.currentMgiIdx = targetMgiIdx;
    updateTitleBarTexts();
    state.playback.currentTime = realOffsetMsec / 1000;
    state.ui.analytics.tmpCalcBase = realOffsetMsec;
    nextTick(() => {
      // 元々再生中だった場合は、再生を再開
      if (playState === 'playing') {
        control.playUI();
      }
      state.movieList.current.isCurrentMovieReady = true;
    });
  };
  const playPreviousMovie = async() => {
    if (state.movieList.current.isTryingToPlayAnotherMovie || state.movieList.current.isFirstMovie) return;

    state.movieList.current.isTryingToPlayAnotherMovie = true;
    // setCurrentMovie が例外を投げてもロックが残らないよう finally で解除する
    try {
      const prevMovieIdx = state.movieList.current.currentMovieIdx - 1;
      const prevMovie = state.movieList.listSelection.currentMovieList?.movies[prevMovieIdx];
      if (!prevMovie) return;

      const offsetMsec = state.playback.videoControlProperties.isPlayBackward ? (prevMovie.durationMsec || 0) : 0;
      await setCurrentMovie(
        prevMovie,
        prevMovieIdx,
        offsetMsec,
      );
    } finally {
      state.movieList.current.isTryingToPlayAnotherMovie = false;
    }
  };
  const playNextMovie = async() => {
    if (state.movieList.current.isTryingToPlayAnotherMovie || state.movieList.current.isLastMovie) return;

    state.movieList.current.isTryingToPlayAnotherMovie = true;
    // setCurrentMovie が例外を投げてもロックが残らないよう finally で解除する
    try {
      const nextMovieIdx = state.movieList.current.currentMovieIdx + 1;
      const nextMovie = state.movieList.listSelection.currentMovieList?.movies[nextMovieIdx];
      if (!nextMovie) return;

      await setCurrentMovie(
        nextMovie,
        nextMovieIdx,
        0,
      );
    } finally {
      state.movieList.current.isTryingToPlayAnotherMovie = false;
    }
  };
  const setVideoControlPropertiesToDefault = () => {
    state.playback.videoControlProperties = {
      playSpeed: 1.0,
      volume: 0,
      play: 0,
      pause: 0,
      tickMsec: 0,
      reset: 0,
      loadMovie: 0,
      isPlayBackward: false,
    };
  };
  const findFirstSeekPoint = (): number => {
    if (!state.movieList.listSelection.currentMovieList) return 0;

    // start_offsetが指定された場合はそこから再生する
    const startOffset = state.movieList.listSelection.currentMovieList.startOffset;
    if (startOffset !== null && startOffset !== undefined) return startOffset;

    return findFirstSeekPointFromHitMgiMap(state.movieList.listSelection.currentMovieList);
  };

  return {
    invalidateCurrentMovie,
    resetVideoPlayers,
    setCurrentMovie,
    playPreviousMovie,
    playNextMovie,
    setVideoControlPropertiesToDefault,
    findFirstSeekPoint,
    trySetNextMovieGeoIndex,
  };
}
